import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { platformChoices, PLATFORM_LABELS, CATEGORIES } from '@affily/shared/constants';
import { bpsToPercent, percentToBps, formatCount } from '@affily/shared/money';
import { formatDay } from '@affily/shared/programDates';
import { api } from '../../lib/api.js';
import { rule, nestedError } from '../../lib/fieldRules.js';
import { useFetch } from '../../hooks/useFetch.js';
import { DashboardLayout } from '../../components/DashboardLayout.jsx';
import { TransparencyBadge } from '../../components/Transparency.jsx';
import { DraftBanner, UnpublishControl } from '../../components/DraftBanner.jsx';
import { SocialConnect } from '../../components/SocialConnect.jsx';
import { UploadButton } from '../../components/UploadButton.jsx';
import {
  Button,
  Field,
  Input,
  Textarea,
  Select,
  Alert,
  Badge,
  Spinner,
} from '../../components/ui.jsx';

const AGE_BUCKETS = ['13-17', '18-24', '25-34', '35-44', '45-54', '55+'];

const EMPTY = {
  displayName: '',
  headline: '',
  bio: '',
  avatarUrl: '',
  location: '',
  niches: [],
  platforms: [],
  demographics: { topCountries: [], ageSplit: [] },
  startingRates: [],
  portfolio: [],
  pastCollaborations: [],
  isAvailable: true,
};

/** Form values are strings; the API takes integers. Convert at the boundary only. */
const toInt = (v) => (v === '' || v === null || v === undefined ? null : Math.round(Number(v)));

export default function CreatorProfileEditor() {
  const { data, loading, reload } = useFetch('/creators/me/profile');
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pictureError, setPictureError] = useState('');
  const [copying, setCopying] = useState(null);
  const { data: social } = useFetch('/connections/social');

  const existing = data?.creator ?? null;
  // Connected accounts with a picture, to use as the profile's own.
  const pictureAccounts = (social?.accounts ?? []).filter((a) => a.avatarUrl && a.status !== 'revoked');

  useEffect(() => {
    if (!existing) return;
    setForm({
      ...EMPTY,
      ...existing,
      headline: existing.headline ?? '',
      bio: existing.bio ?? '',
      avatarUrl: existing.avatarUrl ?? '',
      location: existing.location ?? '',
      platforms: existing.platforms.map((p) => ({
        ...p,
        handle: p.handle ?? '',
        url: p.url ?? '',
        followers: p.followers ?? '',
        avgViews: p.avgViews ?? '',
        engagementPct: p.engagementRateBps != null ? String(bpsToPercent(p.engagementRateBps)) : '',
      })),
      startingRates: existing.startingRates.map((r) => ({
        deliverable: r.deliverable,
        amount: String(r.amountCents / 100),
        note: r.note ?? '',
      })),
      demographics: {
        topCountries: existing.demographics.topCountries ?? [],
        ageSplit: existing.demographics.ageSplit ?? [],
      },
    });
  }, [existing]);

  const set = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    setSaved(false);
  };

  const setAvatar = (url) => {
    setForm((f) => ({ ...f, avatarUrl: url }));
    setSaved(false);
  };

  async function copyPictureFrom(account) {
    setCopying(account.id);
    setPictureError('');
    try {
      setAvatar((await api.post(`/connections/social/${account.id}/picture`)).url);
    } catch (err) {
      setPictureError(err.message);
    } finally {
      setCopying(null);
    }
  }

  const updateList = (key, index, patch) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].map((item, i) => (i === index ? { ...item, ...patch } : item)),
    }));

  const addTo = (key, item) => setForm((f) => ({ ...f, [key]: [...f[key], item] }));
  const removeFrom = (key, index) =>
    setForm((f) => ({ ...f, [key]: f[key].filter((_, i) => i !== index) }));

  const updateDemo = (key, index, patch) =>
    setForm((f) => ({
      ...f,
      demographics: {
        ...f.demographics,
        [key]: f.demographics[key].map((x, i) => (i === index ? { ...x, ...patch } : x)),
      },
    }));

  function buildPayload() {
    return {
      displayName: form.displayName,
      headline: form.headline || undefined,
      bio: form.bio || undefined,
      avatarUrl: form.avatarUrl,
      location: form.location || undefined,
      niches: form.niches,
      // Verified rows are read from the platform and kept by the server as they are.
      platforms: form.platforms
        .filter((p) => p.platform && p.isSelfReported !== false)
        .map((p) => ({
          platform: p.platform,
          handle: p.handle || undefined,
          url: p.url || undefined,
          followers: toInt(p.followers),
          avgViews: toInt(p.avgViews),
          engagementRateBps:
            p.engagementPct === '' ? null : percentToBps(Number(p.engagementPct)),
        })),
      demographics: {
        topCountries: form.demographics.topCountries
          .filter((x) => x.code?.length === 2)
          .map((x) => ({ code: x.code.toUpperCase(), pct: Number(x.pct) || 0 })),
        ageSplit: form.demographics.ageSplit
          .filter((x) => x.bucket)
          .map((x) => ({ bucket: x.bucket, pct: Number(x.pct) || 0 })),
      },
      startingRates: form.startingRates
        .filter((r) => r.deliverable)
        .map((r) => ({
          deliverable: r.deliverable,
          amountCents: Math.round(Number(r.amount || 0) * 100),
          note: r.note || undefined,
        })),
      portfolio: form.portfolio.filter((p) => p.url),
      pastCollaborations: form.pastCollaborations.filter((p) => p.brandName),
      isAvailable: form.isAvailable,
    };
  }

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    setFieldErrors({});
    try {
      const payload = buildPayload();
      if (existing) await api.patch('/creators/me', payload);
      else await api.post('/creators', payload);
      setSaved(true);
      reload();
    } catch (err) {
      setError(err.message);
      setFieldErrors(err.details ?? {});
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <DashboardLayout side="creator">
        <Spinner />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      side="creator"
      title="Public profile"
      description="Everything here is public. That is what makes brands take the numbers seriously."
      actions={
        existing && (
          <Button to={`/creators/${existing.slug}`} variant="outline" size="sm">
            View public page
          </Button>
        )
      }
    >
      <DraftBanner profile={existing} kind="creator" onPublished={reload} />

      <form onSubmit={onSubmit} className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="space-y-6">
          <Alert>{error}</Alert>
          {saved && <Alert tone="positive">Saved.</Alert>}

          <section className="space-y-4">
            <h2 className="text-sm font-semibold">Basics</h2>

            <Field
              label="Display name"
              required
              error={fieldErrors.displayName}
              constraint={rule.max(120)}
            >
              <Input value={form.displayName} onChange={set('displayName')} required maxLength={120} />
            </Field>

            <Field
              label="Headline"
              error={fieldErrors.headline}
              constraint={rule.max(160)}
              hint="One line. What you make, and who for."
            >
              <Input
                value={form.headline}
                onChange={set('headline')}
                maxLength={160}
                placeholder="Skincare routines for oily, acne-prone skin"
              />
            </Field>

            <Field label="Bio" error={fieldErrors.bio} constraint={rule.max(3000)}>
              <Textarea value={form.bio} onChange={set('bio')} rows={4} maxLength={3000} />
            </Field>

            <Field label="Location" error={fieldErrors.location} constraint={rule.max(120)}>
              <Input value={form.location} onChange={set('location')} placeholder="Lisbon, Portugal" />
            </Field>

            <fieldset>
              <legend className="mb-1.5 text-sm font-medium">Logo / profile pic</legend>
              <div className="flex items-center gap-4">
                {form.avatarUrl ? (
                  <img
                    src={form.avatarUrl}
                    alt=""
                    className="size-16 shrink-0 rounded-xl border border-rule object-cover"
                  />
                ) : (
                  <div className="size-16 shrink-0 rounded-xl border border-dashed border-rule-strong" />
                )}
                <div className="flex flex-wrap gap-2">
                  <UploadButton
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    onUploaded={({ url }) => setAvatar(url)}
                    onError={setPictureError}
                  >
                    Upload
                  </UploadButton>
                  {pictureAccounts.map((a) => (
                    <Button
                      key={a.id}
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={copying !== null}
                      onClick={() => copyPictureFrom(a)}
                    >
                      {copying === a.id ? 'Copying…' : `Use my ${PLATFORM_LABELS[a.platform]} picture`}
                    </Button>
                  ))}
                  {form.avatarUrl && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => setAvatar('')}>
                      Remove
                    </Button>
                  )}
                </div>
              </div>
              {(pictureError || fieldErrors.avatarUrl) && (
                <p className="mt-1.5 text-sm text-negative">{pictureError || fieldErrors.avatarUrl}</p>
              )}
              <p className="mt-1.5 text-xs text-ink-faint">Square works best. Saved with the rest of the form.</p>
            </fieldset>

            <fieldset>
              <legend className="mb-1.5 text-sm font-medium">Niches</legend>
              <div className="flex flex-wrap gap-1.5">
                {CATEGORIES.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        niches: f.niches.includes(c)
                          ? f.niches.filter((x) => x !== c)
                          : [...f.niches, c],
                      }))
                    }
                  >
                    <Badge tone={form.niches.includes(c) ? 'accent' : 'neutral'} className="capitalize">
                      {c}
                    </Badge>
                  </button>
                ))}
              </div>
            </fieldset>
          </section>

          {/* ── Platforms ───────────────────────────────────────────────── */}
          <section className="space-y-3">
            <div>
              <h2 className="text-sm font-semibold">Platforms &amp; audience</h2>
              <p className="mt-0.5 text-xs text-ink-muted">
                Connect an account and its followers come from the platform, marked verified and
                refreshed daily. You need one to apply to programs. Anything you add by hand —
                a blog, a newsletter — publishes as self-reported.
              </p>
            </div>

            <SocialConnect next="/dashboard/creator/profile" onChanged={reload} />

            {form.platforms.map((p, i) =>
              p.isSelfReported === false ? (
                <VerifiedPlatform key={i} platform={p} />
              ) : (
                <div key={i} className="rounded-lg border border-rule bg-paper-raised p-3">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Platform" error={nestedError(fieldErrors, "platforms", i, "platform")}>
                      <Select
                        value={p.platform ?? ''}
                        onChange={(e) => updateList('platforms', i, { platform: e.target.value })}
                      >
                        <option value="">Choose…</option>
                        {platformChoices([p.platform]).map((pl) => (
                          <option key={pl} value={pl}>
                            {PLATFORM_LABELS[pl]}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <Field
                      label="Handle"
                      error={nestedError(fieldErrors, "platforms", i, "handle")}
                      constraint={rule.max(100)}
                    >
                      <Input
                        value={p.handle}
                        onChange={(e) => updateList('platforms', i, { handle: e.target.value })}
                        placeholder="@yourhandle"
                      />
                    </Field>
                  </div>

                  <Field
                    label="Public profile URL"
                    className="mt-3"
                    error={nestedError(fieldErrors, "platforms", i, "url")}
                    constraint={rule.publicUrl()}
                  >
                    <Input
                      type="url"
                      value={p.url}
                      onChange={(e) => updateList('platforms', i, { url: e.target.value })}
                      placeholder="https://tiktok.com/@yourhandle"
                    />
                  </Field>

                  <div className="mt-3 grid gap-3 sm:grid-cols-3">
                    <Field
                      label="Followers"
                      error={nestedError(fieldErrors, "platforms", i, "followers")}
                      constraint={rule.wholeNumber()}
                    >
                      <Input
                        type="number"
                        min="0"
                        value={p.followers}
                        onChange={(e) => updateList('platforms', i, { followers: e.target.value })}
                      />
                    </Field>
                    <Field
                      label="Avg. views"
                      error={nestedError(fieldErrors, "platforms", i, "avgViews")}
                      constraint={rule.wholeNumber()}
                    >
                      <Input
                        type="number"
                        min="0"
                        value={p.avgViews}
                        onChange={(e) => updateList('platforms', i, { avgViews: e.target.value })}
                      />
                    </Field>
                    <Field
                      label="Engagement (%)"
                      error={nestedError(fieldErrors, "platforms", i, "engagementRateBps")}
                      constraint={rule.percent()}
                    >
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={p.engagementPct}
                        onChange={(e) => updateList('platforms', i, { engagementPct: e.target.value })}
                      />
                    </Field>
                  </div>

                  <button
                    type="button"
                    onClick={() => removeFrom('platforms', i)}
                    className="mt-2 text-xs text-negative hover:underline"
                  >
                    Remove platform
                  </button>
                </div>
              ),
            )}

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                addTo('platforms', {
                  platform: '',
                  handle: '',
                  url: '',
                  followers: '',
                  avgViews: '',
                  engagementPct: '',
                })
              }
            >
              Add platform
            </Button>
          </section>

          {/* ── Rates ───────────────────────────────────────────────────── */}
          <section className="space-y-3">
            <div>
              <h2 className="text-sm font-semibold">Starting rates · public</h2>
              <p className="mt-0.5 text-xs text-ink-muted">
                The single highest-weighted thing you can publish. Brands filter on it.
              </p>
            </div>

            {form.startingRates.map((r, i) => (
              <div key={i} className="flex flex-wrap items-end gap-3 rounded-lg border border-rule bg-paper-raised p-3">
                <Field
                  label="Deliverable"
                  className="min-w-48 flex-1"
                  error={nestedError(fieldErrors, "startingRates", i, "deliverable")}
                  constraint={rule.max(120)}
                >
                  <Input
                    value={r.deliverable}
                    onChange={(e) => updateList('startingRates', i, { deliverable: e.target.value })}
                    placeholder="TikTok video"
                  />
                </Field>
                <Field
                  label="From (USD)"
                  className="w-32"
                  error={nestedError(fieldErrors, "startingRates", i, "amountCents")}
                  constraint={rule.money()}
                >
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={r.amount}
                    onChange={(e) => updateList('startingRates', i, { amount: e.target.value })}
                  />
                </Field>
                <button
                  type="button"
                  onClick={() => removeFrom('startingRates', i)}
                  className="pb-2.5 text-xs text-negative hover:underline"
                >
                  Remove
                </button>
              </div>
            ))}

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => addTo('startingRates', { deliverable: '', amount: '', note: '' })}
            >
              Add rate
            </Button>
          </section>

          {/* ── Demographics ────────────────────────────────────────────── */}
          <section className="space-y-3">
            <h2 className="text-sm font-semibold">Audience demographics</h2>

            <div className="space-y-2">
              <p className="text-xs font-medium text-ink-muted">Top countries</p>
              {form.demographics.topCountries.map((row, i) => (
                <div key={i} className="flex items-end gap-3">
                  <Field
                    label="Country (ISO-2)"
                    className="w-32"
                    error={nestedError(fieldErrors, "demographics", "topCountries", i, "code")}
                    constraint={rule.countryCode()}
                  >
                    <Input
                      value={row.code ?? ''}
                      maxLength={2}
                      onChange={(e) => updateDemo('topCountries', i, { code: e.target.value.toUpperCase() })}
                      placeholder="US"
                    />
                  </Field>
                  <Field label="Share (%)" className="w-28" constraint={rule.percent()}>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={row.pct ?? ''}
                      onChange={(e) => updateDemo('topCountries', i, { pct: e.target.value })}
                    />
                  </Field>
                  <button
                    type="button"
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        demographics: {
                          ...f.demographics,
                          topCountries: f.demographics.topCountries.filter((_, x) => x !== i),
                        },
                      }))
                    }
                    className="pb-2.5 text-xs text-negative hover:underline"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    demographics: {
                      ...f.demographics,
                      topCountries: [...f.demographics.topCountries, { code: '', pct: '' }],
                    },
                  }))
                }
              >
                Add country
              </Button>
            </div>

            <div className="space-y-2">
              <p className="text-xs font-medium text-ink-muted">Age split</p>
              {form.demographics.ageSplit.map((row, i) => (
                <div key={i} className="flex items-end gap-3">
                  <Field label="Age" className="w-32">
                    <Select
                      value={row.bucket ?? ''}
                      onChange={(e) => updateDemo('ageSplit', i, { bucket: e.target.value })}
                    >
                      <option value="">Choose…</option>
                      {AGE_BUCKETS.map((b) => (
                        <option key={b} value={b}>
                          {b}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="Share (%)" className="w-28" constraint={rule.percent()}>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={row.pct ?? ''}
                      onChange={(e) => updateDemo('ageSplit', i, { pct: e.target.value })}
                    />
                  </Field>
                  <button
                    type="button"
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        demographics: {
                          ...f.demographics,
                          ageSplit: f.demographics.ageSplit.filter((_, x) => x !== i),
                        },
                      }))
                    }
                    className="pb-2.5 text-xs text-negative hover:underline"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    demographics: {
                      ...f.demographics,
                      ageSplit: [...f.demographics.ageSplit, { bucket: '', pct: '' }],
                    },
                  }))
                }
              >
                Add age band
              </Button>
            </div>
          </section>

          {/* ── Portfolio & collabs ─────────────────────────────────────── */}
          <section className="space-y-3">
            <h2 className="text-sm font-semibold">UGC portfolio</h2>
            {form.portfolio.map((item, i) => (
              <div key={i} className="flex flex-wrap items-end gap-3 rounded-lg border border-rule bg-paper-raised p-3">
                <Field
                  label="URL"
                  className="min-w-56 flex-1"
                  error={nestedError(fieldErrors, "portfolio", i, "url")}
                  constraint={rule.url()}
                >
                  <Input
                    value={item.url}
                    onChange={(e) => updateList('portfolio', i, { url: e.target.value })}
                  />
                </Field>
                <Field label="Type" className="w-32">
                  <Select
                    value={item.type}
                    onChange={(e) => updateList('portfolio', i, { type: e.target.value })}
                  >
                    <option value="image">Image</option>
                    <option value="video">Video</option>
                    <option value="link">Link</option>
                  </Select>
                </Field>
                <button
                  type="button"
                  onClick={() => removeFrom('portfolio', i)}
                  className="pb-2.5 text-xs text-negative hover:underline"
                >
                  Remove
                </button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => addTo('portfolio', { url: '', type: 'image', caption: '' })}
            >
              Add example
            </Button>
          </section>

          <section className="space-y-3">
            <h2 className="text-sm font-semibold">Past collaborations</h2>
            {form.pastCollaborations.map((item, i) => (
              <div key={i} className="flex flex-wrap items-end gap-3 rounded-lg border border-rule bg-paper-raised p-3">
                <Field
                  label="Brand"
                  className="min-w-40 flex-1"
                  error={nestedError(fieldErrors, "pastCollaborations", i, "brandName")}
                  constraint={rule.max(120)}
                >
                  <Input
                    value={item.brandName}
                    onChange={(e) => updateList('pastCollaborations', i, { brandName: e.target.value })}
                  />
                </Field>
                <Field label="Link" className="min-w-40 flex-1">
                  <Input
                    value={item.url ?? ''}
                    onChange={(e) => updateList('pastCollaborations', i, { url: e.target.value })}
                  />
                </Field>
                <button
                  type="button"
                  onClick={() => removeFrom('pastCollaborations', i)}
                  className="pb-2.5 text-xs text-negative hover:underline"
                >
                  Remove
                </button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => addTo('pastCollaborations', { brandName: '', url: '', note: '' })}
            >
              Add collaboration
            </Button>
          </section>

          <label className="flex items-start gap-2.5 rounded-lg border border-rule bg-paper-raised p-3">
            <input
              type="checkbox"
              checked={form.isAvailable}
              onChange={set('isAvailable')}
              className="mt-0.5 size-4 accent-accent"
            />
            <span>
              <span className="block text-sm font-medium">Available for partnerships</span>
              <span className="block text-xs text-ink-muted">
                Turn this off when you’re booked. Your profile stays public.
              </span>
            </span>
          </label>

          <div className="border-t border-rule pt-5">
            <Button type="submit" disabled={busy}>
              {busy ? 'Saving…' : existing ? 'Save profile' : 'Create profile'}
            </Button>
            <UnpublishControl profile={existing} kind="creator" onChanged={reload} />
          </div>
        </div>

        <aside className="lg:sticky lg:top-8 lg:self-start">
          <div className="rounded-lg border border-rule bg-paper-raised p-4">
            <h2 className="text-2xs font-semibold uppercase tracking-wider text-ink-faint">
              Transparency score
            </h2>
            {existing ? (
              <>
                <div className="mt-3">
                  <TransparencyBadge transparency={existing.transparency} />
                </div>
                <Checklist criteria={existing.transparency.criteria} />
              </>
            ) : (
              <p className="mt-2 text-sm text-ink-muted">
                Save your profile to see your score and what’s still missing.
              </p>
            )}
            <p className="mt-4 border-t border-rule pt-3 text-xs text-ink-faint">
              This score measures disclosure, not performance, and nothing about it is purchasable.{' '}
              <Link to="/transparency" className="text-accent hover:underline">
                Read the rubric
              </Link>
            </p>
          </div>
        </aside>
      </form>
    </DashboardLayout>
  );
}

/** The unmet criteria, richest first — a concrete to-do list, not a vague nudge. */
function Checklist({ criteria = [] }) {
  const missing = [...criteria].filter((c) => !c.met).sort((a, b) => b.weight - a.weight);

  if (missing.length === 0) {
    return (
      <p className="mt-3 text-sm text-positive">
        Everything is filled in. Nothing left to add.
      </p>
    );
  }

  return (
    <div className="mt-4">
      <p className="text-xs font-medium text-ink">Worth the most right now</p>
      <ul className="mt-2 space-y-2">
        {missing.slice(0, 6).map((c) => (
          <li key={c.key} className="text-xs">
            <span className="flex items-baseline justify-between gap-2">
              <span className="font-medium text-ink">{c.label}</span>
              <span className="shrink-0 tabular-nums text-accent">+{c.weight}</span>
            </span>
            <span className="block text-ink-faint">{c.hint}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** A row read from a connected account: shown, not edited. Disconnecting the account frees it. */
function VerifiedPlatform({ platform: p }) {
  const figures = [
    p.followers != null && `${formatCount(p.followers)} followers`,
    p.avgViews != null && `${formatCount(p.avgViews)} avg. views`,
    p.engagementRateBps != null && `${bpsToPercent(p.engagementRateBps)}% engagement`,
  ].filter(Boolean);
  return (
    <div className="rounded-lg border border-rule bg-paper-raised p-3">
      <p className="text-sm">
        <span className="font-medium">{PLATFORM_LABELS[p.platform]}</span>
        {p.handle && <span className="text-ink-muted"> · {p.handle}</span>}
        <Badge tone="positive" className="ml-2">
          Verified
        </Badge>
      </p>
      <p className="mt-1 text-sm text-ink-muted">{figures.join(' · ') || 'No numbers read yet'}</p>
      <p className="mt-1 text-xs text-ink-faint">
        Read from {PLATFORM_LABELS[p.platform]}
        {p.verifiedAt && ` on ${formatDay(p.verifiedAt)}`}. Disconnect the account above to edit
        these by hand.
      </p>
    </div>
  );
}
